import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Package } from "lucide-react";

import { PageContainer } from "~/components/layout/PageContainer";
import { PageHeader } from "~/components/layout/PageHeader";
import { Badge } from "~/components/ui/badge";
import { Button } from "~/components/ui/button";
import { Card, CardContent } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { TableSkeleton } from "~/components/ui/skeletons";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "~/components/ui/tabs";
import { inventoryOperationsService } from "~/services/inventory/operationsService";
import {
  INV_CATEGORY_LABELS,
  INV_SOURCE_KIND_LABELS,
  INV_TRACKING_LABELS,
  InvProductCategory,
  inventoryProductService,
} from "~/services/inventory/productService";
import { formatCurrency, formatDate } from "~/utils/helpers";

const formatQty = (n: number | null | undefined) =>
  n == null ? "0" : new Intl.NumberFormat("es-CL", { maximumFractionDigits: 2 }).format(n);

const money = (n: number | null | undefined) => (n == null ? "-" : `$${formatCurrency(n)}`);

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="space-y-0.5">
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-sm text-gray-800">{value ?? "-"}</p>
    </div>
  );
}

export function InventoryProductDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["inv-product-detail", id],
    queryFn: () => inventoryProductService.getDetail(id),
    enabled: !!id,
  });

  const { data: moves = [] } = useQuery({
    queryKey: ["inv-product-kardex", id],
    queryFn: () => inventoryOperationsService.getKardex({ productId: id }),
    enabled: !!id,
  });

  const p = data?.product;
  const extra = (p?.extra ?? {}) as Record<string, unknown>;
  const stock = data?.stock_by_location ?? [];
  const lots = data?.lots ?? [];

  const extraFields = useMemo(() => {
    if (!p) return [] as { label: string; value: React.ReactNode }[];
    const s = (k: string) => (extra[k] == null || extra[k] === "" ? "-" : String(extra[k]));
    const n = (k: string) => (extra[k] == null ? "-" : money(Number(extra[k])));
    if (p.source_kind === "finished_good") {
      return [
        { label: "Nombre largo", value: s("long_name") },
        { label: "SKU", value: s("sku") },
        { label: "Código de barras", value: s("barcode") },
        { label: "Grupo", value: s("group") },
        { label: "Subgrupo", value: s("subgroup") },
        { label: "Precio neto", value: n("net_price") },
        { label: "Precio sugerido", value: n("suggested_price") },
        { label: "Costo (catálogo)", value: n("unit_cost_clp") },
        { label: "Peso (kg)", value: s("weight_kg") },
        { label: "Volumen (cm³)", value: s("volume_cm3") },
      ];
    }
    if (p.source_kind === "raw_material") {
      return [
        { label: "Unidad (catálogo)", value: s("unit") },
        { label: "Stock mínimo", value: s("minimum_stock") },
        { label: "Costo promedio", value: n("average_cost_clp") },
        { label: "Grupo", value: s("group") },
      ];
    }
    return [
      { label: "Densidad (kg/L)", value: s("density_kg_per_liter") },
      { label: "Stock mínimo (L)", value: s("minimum_stock_liters") },
      { label: "Costo promedio (por L)", value: n("average_cost_clp") },
    ];
  }, [p, extra]);

  if (isLoading) {
    return (
      <PageContainer>
        <PageHeader
          title="Detalle de producto"
          back={
            <Button variant="ghost" className="h-8 px-2" onClick={() => navigate("/inventario/productos")}>
              <ArrowLeft className="mr-1 h-4 w-4" /> Volver
            </Button>
          }
        />
        <TableSkeleton rows={8} columns={2} />
      </PageContainer>
    );
  }

  if (!p) {
    return (
      <PageContainer>
        <EmptyState icon={Package} title="Producto no encontrado" />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-4 py-2.5 text-sm text-muted-foreground">
        <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
        <span>
          Módulo de inventario <strong className="font-semibold">nuevo — en pruebas</strong>. Ficha
          del producto: datos maestros y todos sus registros.
        </span>
      </div>

      <PageHeader
        title={p.name}
        description={`${p.code ?? "sin código"} · ${INV_SOURCE_KIND_LABELS[p.source_kind] ?? p.source_kind}`}
        back={
          <Button variant="ghost" className="h-8 px-2" onClick={() => navigate("/inventario/productos")}>
            <ArrowLeft className="mr-1 h-4 w-4" /> Volver
          </Button>
        }
        actions={
          <div className="flex flex-wrap items-center gap-2">
            {p.company_name && (
              <Badge
                variant={/pruebas/i.test(p.company_name) ? "secondary" : "outline"}
                className="font-normal"
              >
                {p.company_name}
              </Badge>
            )}
            {p.category && (
              <Badge variant="secondary" className="font-normal">
                {INV_CATEGORY_LABELS[p.category as InvProductCategory]}
              </Badge>
            )}
          </div>
        }
      />

      {/* KPI row */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {[
          { label: "Stock total", value: `${formatQty(p.total_stock)} ${p.stock_uom}` },
          { label: "Valor total", value: money(p.total_value) },
          { label: "Costo unitario", value: money(p.standard_cost) },
          { label: "Último precio venta", value: money(p.last_sale_price) },
        ].map((k) => (
          <Card key={k.label}>
            <CardContent className="p-4">
              <p className="text-xs font-medium text-muted-foreground">{k.label}</p>
              <p className="mt-1 text-lg font-semibold tabular-nums">{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <Tabs defaultValue="master">
        <TabsList>
          <TabsTrigger value="master">Datos maestros</TabsTrigger>
          <TabsTrigger value="moves">Movimientos</TabsTrigger>
          <TabsTrigger value="lots">Lotes</TabsTrigger>
        </TabsList>

        {/* Master data + stock by location */}
        <TabsContent
          value="master"
          className="space-y-6 animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
        >
          <Card>
            <CardContent className="flex flex-col gap-6 p-5 sm:flex-row">
              <div className="shrink-0">
                {p.image_url ? (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="h-28 w-28 rounded-lg border object-cover"
                    onError={(e) => {
                      (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                  />
                ) : (
                  <div className="flex h-28 w-28 items-center justify-center rounded-lg border bg-muted/40 text-muted-foreground">
                    <Package className="h-8 w-8" />
                  </div>
                )}
              </div>
              <div className="grid flex-1 grid-cols-2 gap-x-6 gap-y-4 md:grid-cols-3">
                <Field label="Código" value={p.code ?? "-"} />
                <Field label="Empresa" value={p.company_name ?? "-"} />
                <Field label="Unidad de medida" value={p.stock_uom} />
                <Field label="Seguimiento" value={INV_TRACKING_LABELS[p.tracking] ?? p.tracking} />
                <Field label="Almacenable" value={p.is_storable ? "Sí" : "No"} />
                <Field label="Estado" value={p.is_active ? "Activo" : "Inactivo"} />
                {p.density_kg_per_liter != null && (
                  <Field label="Densidad (kg/L)" value={formatQty(p.density_kg_per_liter)} />
                )}
                {extraFields.map((f) => (
                  <Field key={f.label} label={f.label} value={f.value} />
                ))}
              </div>
            </CardContent>
          </Card>

          <div>
            <h3 className="mb-2 text-base font-semibold">Stock por ubicación</h3>
            {stock.length === 0 ? (
              <EmptyState icon={Package} title="Sin stock" description="Este producto no tiene existencias." />
            ) : (
              <Card>
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Ubicación</TableHead>
                        <TableHead className="text-right">Cantidad</TableHead>
                        <TableHead className="text-right">Reservado</TableHead>
                        <TableHead className="text-right">Valor</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {stock.map((s) => (
                        <TableRow key={s.location_code}>
                          <TableCell>{s.location_name}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatQty(s.quantity)} {p.stock_uom}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{formatQty(s.reserved)}</TableCell>
                          <TableCell className="text-right tabular-nums">{money(s.value)}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* Movements */}
        <TabsContent
          value="moves"
          className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
        >
          {moves.length === 0 ? (
            <EmptyState icon={Package} title="Sin movimientos" description="Este producto no tiene movimientos." />
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Documento</TableHead>
                        <TableHead>Tipo</TableHead>
                        <TableHead>Ubicación</TableHead>
                        <TableHead className="text-right">Entrada</TableHead>
                        <TableHead className="text-right">Salida</TableHead>
                        <TableHead className="text-right">Saldo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {moves.map((m, i) => (
                        <TableRow key={`${m.documento ?? "op"}-${m.ubicacion}-${i}`}>
                          <TableCell className="whitespace-nowrap text-muted-foreground">
                            {formatDate(m.fecha)}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            {m.documento ? (
                              m.purchase_order_id || m.production_batch_id ? (
                                <button
                                  type="button"
                                  onClick={() =>
                                    navigate(
                                      m.purchase_order_id
                                        ? `/purchasing/orders/${m.purchase_order_id}`
                                        : `/production/batches/${m.production_batch_id}`
                                    )
                                  }
                                  className="text-primary hover:underline"
                                  title={
                                    m.purchase_order_id
                                      ? "Ver orden de compra"
                                      : "Ver lote de producción"
                                  }
                                >
                                  {m.documento}
                                </button>
                              ) : (
                                <span className="text-muted-foreground">
                                  {m.documento}
                                </span>
                              )
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </TableCell>
                          <TableCell className="text-muted-foreground">{m.tipo ?? "-"}</TableCell>
                          <TableCell>{m.ubicacion}</TableCell>
                          <TableCell className="text-right tabular-nums text-green-700">
                            {m.entrada ? formatQty(m.entrada) : ""}
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-red-700">
                            {m.salida ? formatQty(m.salida) : ""}
                          </TableCell>
                          <TableCell className="text-right font-medium tabular-nums">
                            {formatQty(m.saldo)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* Lots */}
        <TabsContent
          value="lots"
          className="animate-in fade-in-0 slide-in-from-bottom-1 duration-300"
        >
          {lots.length === 0 ? (
            <EmptyState icon={Package} title="Sin lotes" description="Este producto no tiene lotes registrados." />
          ) : (
            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Lote</TableHead>
                      <TableHead>Referencia</TableHead>
                      <TableHead>Vencimiento</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lots.map((l) => (
                      <TableRow key={l.name}>
                        <TableCell className="font-medium">{l.name}</TableCell>
                        <TableCell className="text-muted-foreground">{l.ref ?? "-"}</TableCell>
                        <TableCell>{l.expiration_date ? formatDate(l.expiration_date) : "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </PageContainer>
  );
}

export default InventoryProductDetailPage;
